import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TrendingUp, TrendingDown, Wallet, FileSpreadsheet, BookOpen, Plus, Trash2 } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, getUser } from "../lib/api";

export default function Accounting() {
  const nav = useNavigate();
  const user = getUser();
  const [fy, setFy] = useState(null);
  const [dash, setDash] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [tb, setTb] = useState(null);
  const [ie, setIe] = useState(null);
  const [bs, setBs] = useState(null);
  const [g80, setG80] = useState(null);

  // Voucher entry form
  const [vForm, setVForm] = useState({
    voucher_type: "payment", date: new Date().toISOString().slice(0, 10),
    narration: "", reference: "",
    entries: [{ account_code: "", debit: 0, credit: 0 }, { account_code: "", debit: 0, credit: 0 }],
  });

  useEffect(() => {
    if (!user || !["admin", "accountant"].includes(user.role)) { nav("/accounting/login"); return; }
    loadAll();
    // eslint-disable-next-line
  }, [fy]);

  const loadAll = async () => {
    try {
      const q = fy ? `?fy=${fy}` : "";
      const [d, a, v, t, ix, b, g] = await Promise.all([
        api.get(`/accounting/dashboard${q}`),
        api.get(`/accounting/accounts`),
        api.get(`/accounting/vouchers${q}`),
        api.get(`/accounting/reports/trial-balance${q}`),
        api.get(`/accounting/reports/income-expenditure${q}`),
        api.get(`/accounting/reports/balance-sheet${q}`),
        api.get(`/accounting/reports/80g-summary${q}`),
      ]);
      setDash(d.data); setAccounts(a.data); setVouchers(v.data);
      setTb(t.data); setIe(ix.data); setBs(b.data); setG80(g.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load accounting data");
    }
  };

  const submitVoucher = async (e) => {
    e.preventDefault();
    const entries = vForm.entries.filter(en => en.account_code && (Number(en.debit) || Number(en.credit)));
    if (entries.length < 2) return toast.error("Need at least 2 entries");
    try {
      await api.post("/accounting/vouchers", { ...vForm, entries: entries.map(e => ({ ...e, debit: Number(e.debit) || 0, credit: Number(e.credit) || 0 })) });
      toast.success("Voucher posted");
      setVForm({ ...vForm, narration: "", reference: "", entries: [{ account_code: "", debit: 0, credit: 0 }, { account_code: "", debit: 0, credit: 0 }] });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voucher failed");
    }
  };

  if (!user || !["admin", "accountant"].includes(user.role)) return null;

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-1">Accounting ERP</div>
          <h1 className="serif text-3xl md:text-4xl font-semibold text-[#000080]">Books of Account</h1>
          <p className="text-sm text-[#737373] mt-1">FY {dash?.fy_label || "—"} · Indian Standard (Apr–Mar)</p>
        </div>
        <Select value={String(fy || (dash?.fy_label?.split("-")[0] || ""))} onValueChange={(v) => setFy(Number(v))}>
          <SelectTrigger className="w-40" data-testid="fy-select"><SelectValue placeholder="Financial Year" /></SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}-{y + 1}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat icon={TrendingUp} label="Total Income" value={fmt(dash.total_income)} color="#138808" />
          <Stat icon={TrendingDown} label="Total Expense" value={fmt(dash.total_expense)} color="#dc2626" />
          <Stat icon={Wallet} label="Net Surplus" value={fmt(dash.surplus)} color="#FF9933" />
          <Stat icon={FileSpreadsheet} label="Vouchers" value={vouchers.length} color="#000080" />
        </div>
      )}

      <Tabs defaultValue="dashboard" className="bg-white rounded-2xl border border-[#eae5d9] p-4 md:p-6">
        <TabsList className="bg-[#F3EFE7] mb-5 flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="acc-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="acc-tab-vouchers">Vouchers</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="acc-tab-accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="trial" data-testid="acc-tab-trial">Trial Balance</TabsTrigger>
          <TabsTrigger value="ie" data-testid="acc-tab-ie">Income & Expenditure</TabsTrigger>
          <TabsTrigger value="bs" data-testid="acc-tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="80g" data-testid="acc-tab-80g">80G Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          {dash?.chart?.length ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dash.chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eae5d9" />
                  <XAxis dataKey="month" stroke="#737373" />
                  <YAxis stroke="#737373" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                  <Bar dataKey="income" fill="#138808" name="Income" radius={[6,6,0,0]} />
                  <Bar dataKey="expense" fill="#dc2626" name="Expense" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty msg="No data yet for this FY" />}
        </TabsContent>

        <TabsContent value="vouchers">
          <form onSubmit={submitVoucher} className="border border-[#eae5d9] rounded-xl p-5 mb-6 bg-[#FDFBF7]" data-testid="voucher-form">
            <div className="grid md:grid-cols-4 gap-3 mb-4">
              <Select value={vForm.voucher_type} onValueChange={(v) => setVForm({ ...vForm, voucher_type: v })}>
                <SelectTrigger data-testid="vform-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="contra">Contra</SelectItem>
                  <SelectItem value="journal">Journal</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" value={vForm.date} onChange={(e) => setVForm({ ...vForm, date: e.target.value })} data-testid="vform-date" />
              <Input placeholder="Reference / Bill No" value={vForm.reference} onChange={(e) => setVForm({ ...vForm, reference: e.target.value })} data-testid="vform-ref" />
              <Input required placeholder="Narration" value={vForm.narration} onChange={(e) => setVForm({ ...vForm, narration: e.target.value })} data-testid="vform-narration" />
            </div>
            <div className="space-y-2">
              {vForm.entries.map((en, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Select value={en.account_code} onValueChange={(v) => {
                    const arr = [...vForm.entries]; arr[i] = { ...arr[i], account_code: v }; setVForm({ ...vForm, entries: arr });
                  }}>
                    <SelectTrigger className="col-span-6" data-testid={`vform-acc-${i}`}><SelectValue placeholder="Account" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="Debit" value={en.debit || ""}
                    onChange={(e) => { const arr = [...vForm.entries]; arr[i] = { ...arr[i], debit: e.target.value, credit: 0 }; setVForm({ ...vForm, entries: arr }); }}
                    data-testid={`vform-debit-${i}`} />
                  <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="Credit" value={en.credit || ""}
                    onChange={(e) => { const arr = [...vForm.entries]; arr[i] = { ...arr[i], credit: e.target.value, debit: 0 }; setVForm({ ...vForm, entries: arr }); }}
                    data-testid={`vform-credit-${i}`} />
                  <button type="button" onClick={() => { const arr = vForm.entries.filter((_, j) => j !== i); setVForm({ ...vForm, entries: arr.length ? arr : [{ account_code: "", debit: 0, credit: 0 }] }); }} className="col-span-2 text-red-500 text-sm flex items-center gap-1"><Trash2 className="w-4 h-4" /> Remove</button>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-4">
              <button type="button" onClick={() => setVForm({ ...vForm, entries: [...vForm.entries, { account_code: "", debit: 0, credit: 0 }] })} className="text-sm text-[#000080] flex items-center gap-1 font-semibold" data-testid="vform-add-entry">
                <Plus className="w-4 h-4" /> Add Entry
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#737373]">
                  Dr: {fmt(vForm.entries.reduce((s, e) => s + Number(e.debit || 0), 0))} · Cr: {fmt(vForm.entries.reduce((s, e) => s + Number(e.credit || 0), 0))}
                </span>
                <Button type="submit" className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full" data-testid="vform-submit">Post Voucher</Button>
              </div>
            </div>
          </form>
          <Table cols={["Date","Voucher #","Type","Narration","Total","Posted by"]} rows={vouchers.map(v => [v.date, v.voucher_no, v.voucher_type, v.narration, fmt(v.total), v.created_by])} />
        </TabsContent>

        <TabsContent value="accounts">
          <Table cols={["Code","Name","Type","Default"]} rows={accounts.map(a => [a.code, a.name, a.type, a.is_default ? "Yes" : "—"])} />
        </TabsContent>

        <TabsContent value="trial">
          {tb && (
            <>
              <Table cols={["Code","Account","Type","Debit","Credit"]} rows={tb.rows.map(r => [r.code, r.name, r.type, fmt(r.balance_debit), fmt(r.balance_credit)])} />
              <div className="mt-4 flex justify-end gap-8 text-sm font-bold">
                <div>Total Dr: <span className="text-[#138808]">{fmt(tb.total_debit)}</span></div>
                <div>Total Cr: <span className="text-[#dc2626]">{fmt(tb.total_credit)}</span></div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="ie">
          {ie && (
            <div className="grid md:grid-cols-2 gap-6">
              <ReportSection title="Income" rows={ie.income} total={ie.total_income} color="#138808" fmt={fmt} />
              <ReportSection title="Expenditure" rows={ie.expense} total={ie.total_expense} color="#dc2626" fmt={fmt} />
              <div className="md:col-span-2 p-4 rounded-xl bg-[#F3EFE7] border border-[#eae5d9] text-center">
                <div className="text-xs uppercase tracking-wider text-[#737373]">{ie.surplus >= 0 ? "Surplus" : "Deficit"}</div>
                <div className="serif text-3xl font-semibold mt-1" style={{ color: ie.surplus >= 0 ? "#138808" : "#dc2626" }}>{fmt(Math.abs(ie.surplus))}</div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bs">
          {bs && (
            <div className="grid md:grid-cols-2 gap-6">
              <ReportSection title="Assets" rows={bs.assets} total={bs.total_assets} color="#000080" fmt={fmt} />
              <div>
                <ReportSection title="Liabilities" rows={bs.liabilities} total={bs.total_liabilities} color="#FF9933" fmt={fmt} />
                <div className="h-4" />
                <ReportSection title="Equity / Fund" rows={bs.equity} total={bs.total_equity} color="#138808" fmt={fmt} />
              </div>
              <div className={`md:col-span-2 p-4 rounded-xl text-center ${bs.balanced ? "bg-[#138808]/10" : "bg-red-100"}`}>
                <div className="text-sm font-bold" style={{ color: bs.balanced ? "#138808" : "#dc2626" }}>
                  {bs.balanced ? "✓ Books are balanced" : "⚠ Books are NOT balanced — check entries"}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="80g">
          {g80 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <Stat icon={BookOpen} label="Total Donations" value={g80.total_count} color="#000080" />
                <Stat icon={Wallet} label="80G Eligible Amount" value={fmt(g80.total_amount)} color="#FF9933" />
                <Stat icon={FileSpreadsheet} label="FY" value={g80.fy_label} color="#138808" />
              </div>
              <Table cols={["Purpose","Method","Donations","Amount"]} rows={g80.rows.map(r => [r.purpose, r.method, r.count, fmt(r.amount)])} />
              <div className="mt-3 text-xs text-[#737373]">Use this summary for ITR-7 filing & 80G certificate audit.</div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-[#eae5d9] p-5">
      <Icon className="w-5 h-5 mb-3" strokeWidth={1.6} style={{ color }} />
      <div className="serif text-2xl font-semibold text-[#000080]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#737373] mt-1">{label}</div>
    </div>
  );
}

function ReportSection({ title, rows, total, color, fmt }) {
  return (
    <div className="border border-[#eae5d9] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#eae5d9]">
        <h3 className="serif text-xl font-semibold" style={{ color }}>{title}</h3>
        <div className="font-bold" style={{ color }}>{fmt(total)}</div>
      </div>
      {rows.length === 0 ? <div className="text-sm text-[#737373] text-center py-4">No entries</div> :
        <div className="space-y-2 text-sm">
          {rows.map((r, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-[#4A4A4A]">{r.code} · {r.name}</span>
              <span className="font-semibold">{fmt(r.amount)}</span>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

function Empty({ msg }) { return <div className="text-center text-sm text-[#737373] py-12">{msg}</div>; }

function Table({ cols, rows }) {
  if (!rows || rows.length === 0) return <Empty msg="No records yet" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#F3EFE7]">
          {cols.map(c => <th key={c} className="text-left px-3 py-2.5 text-xs uppercase tracking-wider text-[#737373] font-bold">{c}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#eae5d9] hover:bg-[#FDFBF7]">
              {r.map((cell, j) => <td key={j} className="px-3 py-2.5 text-[#1A1A1A]">{cell ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
